<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-card-title class="text-indigo text-button">
            {{ editing ? "Llave criptográfica" : "Crear llave" }}
          </v-card-title>

          <v-alert
            v-if="editing && !isWrapped"
            type="info"
            variant="tonal"
            class="mx-4 mb-2"
            density="comfortable"
          >
            Una llave ya creada no se puede modificar. Sólo las llaves
            <b>encapsuladas</b> admiten cambiar su PIN.
          </v-alert>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <v-row>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Datos de la llave</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col v-if="editing" cols="12" sm="6" lg="3">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="data.id"
                          label="ID"
                          prepend-inner-icon="mdi-identifier"
                          readonly
                        />
                      </v-col>

                      <v-col cols="12" sm="6" lg="3">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.alias"
                          label="Alias"
                          prepend-inner-icon="mdi-tag"
                          :readonly="editing"
                          :disabled="saving"
                          :error-messages="errors.for('alias')"
                          hint="Nombre con el que se identifica la llave en el keystore."
                          persistent-hint
                          @update:model-value="errors.clear('alias')"
                        />
                      </v-col>

                      <v-col cols="12" sm="6" lg="3">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-select
                          v-else
                          v-model="data.type"
                          :items="KEY_TYPES"
                          label="Tipo"
                          prepend-inner-icon="mdi-shape"
                          :readonly="editing"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('type')"
                        />
                      </v-col>

                      <v-col cols="12" sm="6" lg="3">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-autocomplete
                          v-else
                          v-model="data.user_id"
                          v-model:search="userSearch"
                          :items="userItems"
                          :loading="loadingUsers"
                          label="Dueño"
                          prepend-inner-icon="mdi-account"
                          :readonly="editing"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('user_id')"
                          no-data-text="No hay coincidencias"
                          hint="Usuario al que pertenece la llave."
                          persistent-hint
                        />
                      </v-col>
                    </v-row>

                    <v-row v-if="isWrapped">
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-autocomplete
                          v-else
                          v-model="data.wrapping_key_id"
                          v-model:search="wrappingSearch"
                          :items="wrappingItems"
                          :loading="loadingWrapping"
                          label="Llave de encapsulamiento"
                          prepend-inner-icon="mdi-key-link"
                          :readonly="editing"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('wrapping_key_id')"
                          no-data-text="No hay coincidencias"
                        />
                      </v-col>

                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.pin"
                          label="PIN"
                          prepend-inner-icon="mdi-form-textbox-password"
                          :disabled="saving"
                          :rules="[rules.pin]"
                          :error-messages="errors.for('pin')"
                          counter="4"
                          maxlength="4"
                          hint="Máximo 4 caracteres. Protege el material encapsulado."
                          persistent-hint
                          @update:model-value="errors.clear('pin')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn
                v-if="canSave"
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="saving"
                :disabled="loading"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>
                {{ editing ? "Guardar PIN" : "Guardar" }}
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="saving"
                @click="router.push('/admin/keys')"
              >
                {{ canSave ? "Cancelar" : "Volver" }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { KeyCreatePayload, KeyType } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { debounce } from "@/utils/functions";
import { keyTypeLabel } from "@/utils/labels";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const editing = computed(() => !!route.params.id);
const keyId = computed(() => Number(route.params.id));

const KEY_TYPES = (["PERSISTENT", "WRAPPED", "WRAPPING"] as KeyType[]).map((t) => ({
  title: keyTypeLabel(t),
  value: t,
}));

const loading = ref(true);
const saving = ref(false);
const data = ref({
  id: undefined as number | undefined,
  alias: "",
  type: "PERSISTENT" as KeyType,
  user_id: undefined as number | undefined,
  wrapping_key_id: undefined as number | undefined,
  pin: "",
});

const isWrapped = computed(() => data.value.type === "WRAPPED");

/**
 * Qué se puede guardar.
 *
 * Al crear, todo. Al editar, SÓLO el PIN y SÓLO si la llave es WRAPPED:
 * KeyService.updatePinById lanza PreconditionFailedException (412) para
 * cualquier otro tipo, porque el PIN protege el material encapsulado y en una
 * PERSISTENT o WRAPPING no tiene sentido.
 *
 * La versión anterior dejaba el formulario editable y al guardar respondía
 * "No permitido" sin hacer nada: no había forma de cambiar un PIN desde la UI.
 */
const canSave = computed(() => !editing.value || isWrapped.value);

const rules = {
  required: (val: unknown) => (val !== undefined && val !== null && val !== "") || "Campo requerido.",
  // KeyDTO.java:46 -> @Length(max = 4). La regla anterior validaba <= 16, lo que
  // producía un 422 que el usuario no podía interpretar.
  pin: (val: unknown) =>
    !val || String(val).length <= 4 || "El PIN admite como máximo 4 caracteres.",
};

// ---- autocompletado de usuarios -----------------------------------------

const userSearch = ref("");
const loadingUsers = ref(false);
const userItems = ref<Array<{ title: string; value: number }>>([]);

async function searchUsers(term: string) {
  loadingUsers.value = true;
  try {
    const page = await banco.admin.users.find({ ...(term ? { name: term } : {}), size: 50 });
    userItems.value = page.content.map((u) => ({
      title: `${u.name} (${u.username})`,
      value: u.id,
    }));
  } catch (err) {
    report(err, { title: "Usuarios", fallback: "No fue posible buscar usuarios." });
  } finally {
    loadingUsers.value = false;
  }
}
watch(userSearch, debounce((term: string) => void searchUsers(term), 300));

// ---- autocompletado de llaves de encapsulamiento -------------------------

const wrappingSearch = ref("");
const loadingWrapping = ref(false);
const wrappingItems = ref<Array<{ title: string; value: number }>>([]);

async function searchWrappingKeys(term: string) {
  loadingWrapping.value = true;
  try {
    const page = await banco.admin.keys.find({
      type: "WRAPPING",
      ...(term ? { alias: term } : {}),
      size: 20,
    });
    wrappingItems.value = page.content.map((k) => ({
      title: `${k.alias ?? "(sin alias)"} — id ${k.id}`,
      value: k.id,
    }));
  } catch (err) {
    report(err, { title: "Llaves", fallback: "No fue posible buscar llaves." });
  } finally {
    loadingWrapping.value = false;
  }
}
watch(wrappingSearch, debounce((term: string) => void searchWrappingKeys(term), 300));

// ---- carga y guardado ----------------------------------------------------

async function load() {
  // Precargar ambos catálogos: un autocompletado que se abre vacío hasta escribir
  // dos caracteres no comunica que haya nada que elegir. Se conserva la búsqueda
  // remota para instalaciones con muchos usuarios.
  await Promise.all([searchUsers(""), searchWrappingKeys("")]);

  if (!editing.value) {
    loading.value = false;
    return;
  }
  try {
    const key = await banco.admin.keys.get(keyId.value);
    Object.assign(data.value, key);
    // Asegurar que el seleccionado esté entre las opciones aunque no venga en la
    // primera página: si Vuetify no encuentra el valor entre sus items, pinta el
    // id crudo en vez de la etiqueta.
    if (key.user_id && !userItems.value.some((i) => i.value === key.user_id)) {
      const owner = await banco.admin.users.get(key.user_id).catch(() => null);
      if (owner) {
        userItems.value = [{ title: `${owner.name} (${owner.username})`, value: owner.id }, ...userItems.value];
      }
    }
    if (key.wrapping_key_id && !wrappingItems.value.some((i) => i.value === key.wrapping_key_id)) {
      const wk = await banco.admin.keys.get(key.wrapping_key_id).catch(() => null);
      if (wk) {
        wrappingItems.value = [
          { title: `${wk.alias ?? "(sin alias)"} — id ${wk.id}`, value: wk.id },
          ...wrappingItems.value,
        ];
      }
    }
  } catch (err) {
    report(err, { title: "Llave", fallback: "Ocurrió un error al recuperar la llave." });
  } finally {
    loading.value = false;
  }
}

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;
  try {
    if (editing.value) {
      await banco.admin.keys.updatePin(keyId.value, { pin: data.value.pin });
      notifications.success("Se actualizó el PIN de la llave.", "Guardado");
    } else {
      const payload = (
        isWrapped.value
          ? {
              type: "WRAPPED",
              alias: data.value.alias || undefined,
              user_id: data.value.user_id,
              wrapping_key_id: data.value.wrapping_key_id as number,
              pin: data.value.pin || undefined,
            }
          : {
              type: data.value.type,
              alias: data.value.alias || undefined,
              user_id: data.value.user_id,
            }
      ) as KeyCreatePayload;
      await banco.admin.keys.create(payload);
      notifications.success("Se creó la llave exitosamente.", "Creado");
    }
    router.push("/admin/keys");
  } catch (err) {
    report(err, {
      title: "Llave",
      fallback: "Ocurrió un error al guardar la llave.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
